/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import {
    Col,
    Divider,
    PageHeader,
    Row,
    Space,
    Steps,
    Typography,
} from 'antd';
import {
    FC,
    memo,
    useEffect,
    useState,
} from 'react';
import { EVENTS, EVENTS_MAPPED, MICRO_EVENTS_MAPPED } from '@/const/booking.constants';
import { useTranslation } from 'react-i18next';
import { IBooking, IBookingEvent } from '../../interfaces/booking.interface';
import { TimelineType } from './TimelineType';
import CreateStatus from './Timeline/Create';
import AssignStatus from './Timeline/Assign';
import StatusUpdate from './Timeline/StatusUpdate';

interface IProps {
    booking: IBooking;
    loading: boolean;
    events: IBookingEvent[];
    timelineType: boolean;
    handleChangeTimelineType: (type: boolean) => void;
}

interface IEventNode {
    [key: string]: {
        (booking: IBooking, showDetail: boolean, event: IBookingEvent): JSX.Element
    };
}

const { Title, Text } = Typography;

const Timeline: FC<IProps> = ({
    booking,
    loading,
    events,
    timelineType,
    handleChangeTimelineType,
}: IProps) => {
    const { t } = useTranslation('bookings');
    const [eventSelected, setEventSelected] = useState<IBookingEvent>(events[0]);

    const handleChangeEvent = (event: IBookingEvent) => setEventSelected(event);

    const statusComponent: IEventNode = {
        [EVENTS.NEW_BOOKING]:
            (booking: IBooking, showDetail: boolean, bookingEvent: IBookingEvent) => (
                <CreateStatus
                    booking={booking}
                    showDetail={showDetail}
                    bookingEvent={bookingEvent}
                    bookingEventSelected={bookingEvent === eventSelected}
                />
            ),
        [EVENTS.ASSIGN]:
            (booking: IBooking, showDetail: boolean, bookingEvent: IBookingEvent) => (
                <AssignStatus
                    booking={booking}
                    showDetail={showDetail}
                    bookingEvent={bookingEvent}
                    bookingEventSelected={bookingEvent === eventSelected}
                />
            ),
        [EVENTS.STATUS_UPDATE]:
            (booking: IBooking, showDetail: boolean, bookingEvent: IBookingEvent) => (
                <StatusUpdate
                    booking={booking}
                    showDetail={showDetail}
                    bookingEvent={bookingEvent}
                    bookingEvents={events}
                    bookingEventSelected={bookingEvent === eventSelected}
                />
            ),
    };

    const selectedComponent = eventSelected && statusComponent[eventSelected.type];

    useEffect(() => {
        if (events.length > 0) setEventSelected(events[0]);
    }, [events]);

    return (
        <Space direction="vertical" size={12}>
            <PageHeader
                title={(
                    <Title level={5} style={{ marginBottom: 0 }}>
                        {t('DETAIL.STORY_LINE.TITLE')}
                    </Title>
                )}
                extra={[
                    handleChangeTimelineType && (
                        <TimelineType
                            timelineType={timelineType as boolean}
                            handleChangeTimelineType={handleChangeTimelineType}
                        />
                    ),
                ]}
                footer={<Divider />}
            />

            <Text>
                {t('DETAIL.STORY_LINE.DESCRIPTION')}
            </Text>

            <div className="booking__timeline">
                {
                    loading ? (
                        // TODO: Add a loading component
                        <div>Loading...</div>
                    ) : !events?.length ? (
                        // TODO: Add a error component
                        <div>Error</div>
                    ) : (
                        <Row gutter={24} align="top" justify="space-between">
                            <Col span={10}>
                                <div className="booking__timeline--list">
                                    <Steps
                                        progressDot
                                        size="small"
                                        direction="vertical"
                                        current={events.length}
                                        items={
                                            events
                                                .filter((bookingEvent: IBookingEvent) => {
                                                    const isMappedEvent = EVENTS_MAPPED.includes(bookingEvent?.type);
                                                    const isMappedMicroEvent = MICRO_EVENTS_MAPPED.includes(bookingEvent?.newStatusText);
                                                    const isStatusUpdateEvent = bookingEvent?.type === EVENTS.STATUS_UPDATE;

                                                    if (isStatusUpdateEvent) {
                                                        return isMappedMicroEvent;
                                                    }

                                                    return isMappedEvent;
                                                })
                                                .map((bookingEvent: IBookingEvent) => {
                                                    const component = statusComponent[bookingEvent?.type];
                                                    return ({
                                                        title: (
                                                            <div className="booking__timeline--list-item">
                                                                {component && component(booking, false, bookingEvent)}
                                                            </div>
                                                        ),
                                                        onClick: () => handleChangeEvent(bookingEvent),
                                                    });
                                                })
                                        }
                                    />
                                </div>
                            </Col>
                            <Col span={14}>
                                <div className="booking__timeline--detail">
                                    {
                                        selectedComponent && typeof selectedComponent === 'function'
                                            ? selectedComponent(booking, true, eventSelected)
                                            : selectedComponent
                                    }
                                </div>
                            </Col>
                        </Row>
                    )
                }
            </div>
        </Space>
    );
};

export default memo(Timeline);
